/**
 * CompanyContactPersonsField — Ansprechpartner-Verknüpfungen für Firmen (P1).
 *
 * Datenmodell: [{ contact_id, function, is_primary }]
 * Nur sichtbar wenn contact_type == "company" oder "institution".
 * Verlinkt bestehende Kontakte (person) über einen leichten Autocomplete-Search.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Trash, User, ArrowSquareOut } from "@phosphor-icons/react";

export default function CompanyContactPersonsField({ persons, onChange, contactType }) {
  const [items, setItems] = useState(() => Array.isArray(persons) ? [...persons] : []);
  const [contactCache, setContactCache] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [addingRow, setAddingRow] = useState(false);

  useEffect(() => {
    if (Array.isArray(persons)) setItems(persons);
  }, [persons]);

  // Lookup fehlende Kontaktnamen
  useEffect(() => {
    const missing = items
      .map((p) => p.contact_id)
      .filter((cid) => cid && !contactCache[cid]);
    if (!missing.length) return;
    (async () => {
      const map = { ...contactCache };
      for (const cid of missing) {
        try {
          const { data } = await api.get(`/contacts/${cid}`);
          map[cid] = data;
        } catch {
          map[cid] = { id: cid, first_name: "?", last_name: "(nicht gefunden)" };
        }
      }
      setContactCache(map);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const emit = (next) => {
    setItems(next);
    onChange(next);
  };

  const displayName = (c) => {
    if (!c) return "—";
    const nm = [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
    return nm || c.company_name || c.id;
  };

  const doSearch = useMemo(() => {
    let t;
    return (q) => {
      clearTimeout(t);
      if (!q || q.length < 2) { setSearchResults([]); return; }
      setSearching(true);
      t = setTimeout(async () => {
        try {
          const { data } = await api.get(`/contacts?q=${encodeURIComponent(q)}&contact_type=person&limit=8`);
          const list = Array.isArray(data) ? data : (data?.items || []);
          setSearchResults(list.filter((c) => !items.some((it) => it.contact_id === c.id)));
        } finally { setSearching(false); }
      }, 200);
    };
  }, [items]);

  const addPerson = (c) => {
    // 2026-02 — Kein Auto-primär mehr. User entscheidet bewusst.
    emit([...items, { contact_id: c.id, function: "", is_primary: false }]);
    setContactCache((m) => ({ ...m, [c.id]: c }));
    setSearchTerm("");
    setSearchResults([]);
    setAddingRow(false);
  };

  const removePerson = (idx) => {
    // 2026-02 — Kein Auto-Ausweichen mehr auf einen anderen Kontakt.
    // Wenn der primäre entfernt wird, bleibt „kein primärer" gültig.
    emit(items.filter((_, i) => i !== idx));
  };

  const setFunc = (idx, val) => {
    emit(items.map((p, i) => i === idx ? { ...p, function: val } : p));
  };

  const togglePrimary = (idx) => {
    // 2026-02 — Primär optional: Klick auf bereits primär -> Auswahl aufheben,
    // sonst diesen zum einzigen primären machen (max. 1).
    const wasPrimary = !!items[idx]?.is_primary;
    emit(items.map((p, i) => ({
      ...p,
      is_primary: wasPrimary ? false : i === idx,
    })));
  };

  if (!["company", "institution", "court", "authority_office"].includes(contactType)) return null;

  return (
    <div className="border border-slate-200 rounded-md p-3 bg-slate-50/50" data-testid="contact-company-persons">
      <div className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold mb-2 flex items-center gap-1.5">
        <User size={12} /> Ansprechpartner (verknüpfte Kontakte)
      </div>

      {items.length === 0 && (
        <div className="text-xs text-slate-500 italic mb-2">
          Noch keine Ansprechpartner verknüpft.
        </div>
      )}

      <div className="space-y-1.5">
        {items.map((p, idx) => {
          const c = contactCache[p.contact_id];
          return (
            <div key={p.contact_id + idx} className="grid grid-cols-[1fr_1fr_auto_auto] gap-1.5 items-center bg-white border border-slate-200 rounded-md p-2">
              <div className="text-sm">
                <div className="font-medium text-slate-900 truncate">{displayName(c)}</div>
                <a
                  href={`/kontakte?open=${p.contact_id}`}
                  target="_blank" rel="noopener noreferrer"
                  className="text-[10px] text-emerald-700 hover:underline inline-flex items-center gap-0.5 font-mono"
                >
                  {p.contact_id.slice(0, 8)} <ArrowSquareOut size={9} />
                </a>
              </div>
              <Input
                placeholder="Funktion (z.B. Geschäftsführung)"
                value={p.function || ""}
                onChange={(e) => setFunc(idx, e.target.value)}
                className="h-8 text-xs"
                data-testid={`company-person-function-${idx}`}
              />
              <label className="text-[10px] text-slate-600 flex items-center gap-1 cursor-pointer" title="Als primär markieren – erneut klicken hebt die Markierung auf">
                <input
                  type="checkbox"
                  checked={!!p.is_primary}
                  onChange={() => togglePrimary(idx)}
                  className="cursor-pointer accent-emerald-600"
                  data-testid={`company-person-primary-${idx}`}
                />
                primär
              </label>
              <Button
                type="button" size="icon" variant="ghost"
                onClick={() => removePerson(idx)}
                className="h-7 w-7 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                title="Verknüpfung lösen"
                data-testid={`company-person-remove-${idx}`}
              >
                <Trash size={13} />
              </Button>
            </div>
          );
        })}
      </div>

      {addingRow ? (
        <div className="mt-2 space-y-1.5">
          <Input
            placeholder="Kontakt suchen (mind. 2 Zeichen)…"
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); doSearch(e.target.value); }}
            autoFocus
            className="h-9 text-sm"
            data-testid="company-person-search"
          />
          {searching && <div className="text-[10px] text-slate-400 italic">suche…</div>}
          {searchResults.length > 0 && (
            <div className="border border-slate-200 rounded-md bg-white shadow-sm max-h-56 overflow-y-auto">
              {searchResults.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => addPerson(c)}
                  className="w-full text-left px-3 py-1.5 text-xs hover:bg-emerald-50 border-b last:border-b-0 border-slate-100"
                  data-testid={`company-person-search-result-${c.id}`}
                >
                  <div className="font-medium text-slate-900">{displayName(c)}</div>
                  {c.emails?.[0] && (
                    <div className="text-[10px] text-slate-500">
                      {typeof c.emails[0] === "string" ? c.emails[0] : c.emails[0].email}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
          {searchTerm.length >= 2 && !searching && searchResults.length === 0 && (
            <div className="text-[10px] text-slate-500 italic">
              Kein Kontakt gefunden. Bitte zuerst als Person anlegen.
            </div>
          )}
          <Button
            type="button" size="sm" variant="ghost"
            onClick={() => { setAddingRow(false); setSearchTerm(""); setSearchResults([]); }}
            className="h-7 text-xs"
          >
            Abbrechen
          </Button>
        </div>
      ) : (
        <Button
          type="button" size="sm" variant="outline"
          onClick={() => setAddingRow(true)}
          className="h-7 text-xs gap-1 mt-2"
          data-testid="company-person-add-btn"
        >
          <Plus size={12} /> Ansprechpartner verknüpfen
        </Button>
      )}
    </div>
  );
}
